/**
 * Save-time validation for a Connection's authentication section.
 *
 * The Edit Connection page and the flow wizard's embedded connection form show the same
 * two auth shapes, so the rules live here instead of once per screen. They previously had
 * a copy each, the copies drifted, and each grew its own defects (OT-4850).
 *
 * A connector either ships auth schemas (`authItems`), which AuthenticationForm renders as
 * JSON Forms, or only declares `supportedAuthTypes`, for which the screens render a fixed
 * set of inputs per type. The two are mutually exclusive, and only the second knows its
 * authentication type before the save: the schema-driven one derives the type from the
 * selected scheme while building the payload.
 */

/**
 * Names of the fields a schema marks as secret. Secrets are excluded from validation
 * altogether: a stored one arrives as null and is only ever displayed as "*****", so the
 * form cannot tell "unchanged" from "empty", and whether to (re-)enter one is the user's
 * call.
 */
export function secretFieldNames(schema: any): string[] {
  const properties = schema?.properties ?? {};
  return Object.keys(properties).filter(
    (name) => properties[name]?.encrypted || properties[name]?.masked
  );
}

/**
 * JSON Schema `required` only tests that a key is present, and clearing a text input
 * leaves an empty string behind, so emptiness is checked here rather than left to AJV.
 */
function hasValue(value: any): boolean {
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
}

/** Required, non-secret fields of one JSON-Forms-rendered scheme that carry no value. */
export function missingRequiredFields(entry: any): string[] {
  const required: string[] = entry?.schema?.required ?? [];
  const secrets = secretFieldNames(entry?.schema);
  const data = entry?.data ?? {};
  return required.filter(
    (name) => !secrets.includes(name) && !hasValue(data[name])
  );
}

/** Every secret field name in a scheme map, including the schemes nested under OAuth flows. */
function allSecretFieldNames(authMap: any): string[] {
  const names: string[] = [];
  if (!authMap) {
    return names;
  }
  authMap.forEach((entry: any) => {
    names.push(...secretFieldNames(entry?.schema));
    if (entry?.flows) {
      names.push(...allSecretFieldNames(entry.flowData));
    }
  });
  return names;
}

/**
 * Field names an AJV error points at. The bundled AJV reports `dataPath` ("password",
 * ".password"); newer ones report `instancePath` ("/password"). Both separators are split
 * so either shape resolves to the field.
 */
function errorFieldNames(error: any): string[] {
  const path = String(error?.instancePath ?? error?.dataPath ?? "");
  return path.split(/[./[\]]+/).filter(Boolean);
}

/**
 * AJV errors worth blocking a save for. Errors on secret fields are dropped: a stored
 * secret arrives as null, which trips its schema's `type: "string"` even though the
 * credential is present server-side.
 */
function blockingErrors(authMap: any, authErrors: any[]): any[] {
  const secrets = allSecretFieldNames(authMap);
  return (authErrors ?? []).filter(
    (error) => !errorFieldNames(error).some((name) => secrets.includes(name))
  );
}

/** True when every selected scheme in the map has its required fields filled. */
function everySchemeComplete(authMap: any): boolean {
  if (!authMap || authMap.size === 0) {
    return false;
  }
  let complete = true;
  authMap.forEach((entry: any) => {
    if (!entry) {
      complete = false;
      // An OAuth2 scheme carries no schema of its own: its fields live in the schemas of
      // the flows nested under it, so the requirement applies one level down.
    } else if (entry.flows) {
      complete = complete && everySchemeComplete(entry.flowData);
    } else if (missingRequiredFields(entry).length) {
      complete = false;
    }
  });
  return complete;
}

/**
 * Validity of a schema-driven connector's auth. `authMap` is AuthenticationForm's map of
 * selected schemes; `authErrors` are the AJV errors it reports for the forms it renders.
 * An empty map means no scheme has been picked, which the screens mark as required.
 */
export function isSchemaAuthValid(
  authMap: any,
  authErrors: any[] = []
): boolean {
  if (!authMap || authMap.size === 0) {
    return false;
  }
  if (blockingErrors(authMap, authErrors).length) {
    return false;
  }
  return everySchemeComplete(authMap);
}

/**
 * Validity of the fixed per-type inputs a screen renders for a connector that ships no
 * auth schemas. The type is upper-cased because it is persisted in mixed case ("Oauth",
 * "Basic", "Api_key") and loaded back as-is when a saved connection is re-opened. The
 * types that fall through to `true` render no inputs of their own in that UI, so they have
 * nothing to fill in. Passwords and client secrets are held separately and shown as the
 * placeholder, so they are left to the user rather than checked here. Fields go through
 * `hasValue`, so a whitespace-only entry counts as empty here exactly as it does for a
 * schema-driven connector.
 */
export function isLegacyAuthValid(
  authenticationType: string,
  authObject: any
): boolean {
  switch (authenticationType?.toUpperCase()) {
    case "BASIC":
      return (
        hasValue(authObject?.basicAuth?.username) &&
        hasValue(authObject?.basicAuth?.endPoint)
      );
    case "FORM":
      return (
        hasValue(authObject?.formAuth?.form) &&
        hasValue(authObject?.formAuth?.endPoint)
      );
    case "TOKEN":
      return (
        hasValue(authObject?.tokenAuth?.authToken) &&
        hasValue(authObject?.tokenAuth?.endPoint)
      );
    case "API_KEY":
      return (
        hasValue(authObject?.apiKeyAuth?.apiKey) &&
        hasValue(authObject?.apiKeyAuth?.nameInHeader)
      );
    case "OAUTH":
      return hasValue(authObject?.oauth?.clientId);
    case "NONE":
    case "BEARER":
    case "POSTADS":
    case "JWT":
    case "AWS_SIGV4":
    case "WOODWING_STUDIO":
    case "CUSTOM":
      return true;
    default:
      return false;
  }
}

/**
 * Whether a connection's auth section is complete enough to save. `authItems` decides
 * which of the two shapes applies.
 */
export function isAuthValid(params: {
  authItems?: any;
  authMap?: any;
  authErrors?: any[];
  authenticationType?: string;
  authObject?: any;
}): boolean {
  if (params.authItems) {
    return isSchemaAuthValid(params.authMap, params.authErrors);
  }
  return isLegacyAuthValid(params.authenticationType, params.authObject);
}
